/**
 * Firebase fallback for emailForSignIn
 * Saves/retrieves the email from the 'pendingSignIns' Firestore collection
 * when localStorage is unavailable (e.g., user opens magic link on different device)
 */
import { doc, setDoc, getDoc, deleteDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';

const COLLECTION_NAME = 'pendingSignIns';

/**
 * Save email to both localStorage and Firebase pendingSignIns collection
 * Uses a hash of the email as document ID for easy retrieval
 */
export async function savePendingSignInEmail(email: string): Promise<void> {
  // Save to localStorage (primary)
  localStorage.setItem('emailForSignIn', email);

  // Save to Firebase (fallback)
  try {
    const docId = btoa(email).replace(/[/+=]/g, '_');
    await setDoc(doc(db, COLLECTION_NAME, docId), {
      email,
      createdAt: new Date().toISOString(),
    });
    console.log('📧 Email saved to pendingSignIns collection');
  } catch (error) {
    console.error('Error saving to pendingSignIns:', error);
  }
}

/**
 * Retrieve email for sign-in: first from localStorage, then Firebase fallback
 */
export async function getPendingSignInEmail(emailHint?: string): Promise<string | null> {
  // Try localStorage first
  const localEmail = localStorage.getItem('emailForSignIn');
  if (localEmail) return localEmail;

  // If we have an email hint (e.g., from URL params), try Firebase
  if (emailHint) {
    try {
      const docId = btoa(emailHint).replace(/[/+=]/g, '_');
      const docSnap = await getDoc(doc(db, COLLECTION_NAME, docId));
      if (docSnap.exists()) {
        const email = docSnap.data().email;
        // Re-save to localStorage for future use
        localStorage.setItem('emailForSignIn', email);
        return email;
      }
    } catch (error) {
      console.error('Error fetching from pendingSignIns:', error);
    }
  }

  return null;
}

/**
 * Clean up pending sign-in data after successful authentication
 */
export async function clearPendingSignInEmail(email: string): Promise<void> {
  localStorage.removeItem('emailForSignIn');

  try {
    const docId = btoa(email).replace(/[/+=]/g, '_');
    await deleteDoc(doc(db, COLLECTION_NAME, docId));
    console.log('🧹 Cleaned up pendingSignIns entry');
  } catch (error) {
    console.error('Error clearing pendingSignIns:', error);
  }
}
